import { Mutation } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { ID } from 'shared/types/id';

const FIRESTORE_ERROR_CODES = new Set([
  'cancelled',
  'unknown',
  'invalid-argument',
  'deadline-exceeded',
  'not-found',
  'already-exists',
  'permission-denied',
  'resource-exhausted',
  'failed-precondition',
  'aborted',
  'out-of-range',
  'unimplemented',
  'internal',
  'unavailable',
  'data-loss',
  'unauthenticated',
]);

const isFirestoreError = (error: unknown): error is FirebaseError =>
  error instanceof FirebaseError && FIRESTORE_ERROR_CODES.has(error.code);

type VariablesShapeNode =
  | string
  | { [key: string]: VariablesShapeNode | VariablesShapeNode[] };

export type MutationErrorContext = {
  mutationKey: unknown;
  variablesShape: VariablesShapeNode;
  uid: ID | undefined;
};

const MAX_DEPTH = 5;

const getVariablesShape = (value: unknown, depth = 0): VariablesShapeNode => {
  if (value === null) {
    return 'null';
  }
  if (typeof value !== 'object') {
    return typeof value;
  }
  if (depth >= MAX_DEPTH) {
    return 'object[max-depth]';
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return 'array[]';
    }

    const elementShapes = value.map((element) =>
      getVariablesShape(element, depth + 1),
    );
    const uniqueShapes = Array.from(
      new Map(
        elementShapes.map((shape) => [JSON.stringify(shape), shape]),
      ).values(),
    );
    return { __array__: uniqueShapes };
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, child]) => [
      key,
      getVariablesShape(child, depth + 1),
    ]),
  );
};

export const getMutationErrorContext = (
  error: unknown,
  variables: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
  uid: ID | undefined,
): MutationErrorContext | null => {
  if (!isFirestoreError(error)) {
    return null;
  }

  return {
    mutationKey: mutation.options.mutationKey,
    variablesShape: getVariablesShape(variables),
    uid,
  };
};
